import React, { Fragment, JSX } from 'react';
import Payment from '@/model/Payment';
import { IconCreditCard, IconPix, IconTicketPayment } from '@/assets/icons';
import { classNames } from '@/helpers/common';
import PaymentType from '@/model/PaymentType';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { OptionProps, Select } from '@/components/Select';
import { unmask } from '@/helpers/masks/cash';
import { calculateCreditCardPaymentValue } from '@/helpers/payment/installmentFees';
import { FormInputNamePayment, PaymentContent, PaymentTab } from '../types';

interface StateProps {
  payment?: Payment;
  formData: FormData;
  formErrors: FormErrors;
}

interface DispatchProps {
  toCurrency: (value: number) => string;
  onChangeFormInput: OnChangeFormInput;
}

type Props = StateProps & DispatchProps;

export const PaymentChoiceContent: React.FC<Props> = ({
  payment,
  toCurrency,
  formData,
  onChangeFormInput,
  formErrors,
}): JSX.Element => {
  const mountTabs = (): PaymentTab[] => {
    const tabs: PaymentTab[] = [];
    if (payment && payment.fees) {
      if (payment.allowPaymentCredit) {
        tabs.push({
          text: 'Cartão de Crédito',
          icon: (
            <IconCreditCard
              className={classNames(
                formData[FormInputNamePayment.paymentType] &&
                  Number(formData[FormInputNamePayment.paymentType]) === PaymentType.CREDIT_CARD
                  ? 'text-white'
                  : 'text-neutro-w-600',
                'mb-[16px] h-[15px]',
              )}
            />
          ),
          type: PaymentType.CREDIT_CARD,
        });
      }

      if (payment.allowPaymentBankSlip) {
        tabs.push({
          text: 'Boleto',
          icon: (
            <IconTicketPayment
              className={classNames(
                formData[FormInputNamePayment.paymentType] &&
                  Number(formData[FormInputNamePayment.paymentType]) === PaymentType.BANK_SLIP
                  ? 'text-white'
                  : 'text-neutro-w-600',
                'mb-[16px] h-[22px]',
              )}
            />
          ),
          type: PaymentType.BANK_SLIP,
        });
      }

      if (payment.allowPaymentPIX) {
        tabs.push({
          text: 'PIX',
          icon: (
            <IconPix
              className={classNames(
                formData[FormInputNamePayment.paymentType] &&
                  Number(formData[FormInputNamePayment.paymentType]) === PaymentType.PIX
                  ? 'text-white'
                  : 'text-neutro-w-600',
                'mb-[16px] h-[20px]',
              )}
            />
          ),
          type: PaymentType.PIX,
        });
      }
    }
    return tabs;
  };
  const options: OptionProps[] = [];

  if (payment && payment.fees && payment.fees.installmentFees.length > 0) {
    if (
      formData[FormInputNamePayment.paymentValue] &&
      Number(formData[FormInputNamePayment.paymentValue]) > 0
    ) {
      const totalValue = Number(formData[FormInputNamePayment.paymentValue]);
      payment.fees.installmentFees.forEach(({ installment, fee }) => {
        const value = calculateCreditCardPaymentValue(
          totalValue,
          payment.fees.credit,
          fee,
          installment,
        );
        const option: OptionProps = {
          value: `${installment}`,
          label: `${installment} X ${toCurrency(value / installment)}`,
        };
        options.push(option);
      });
    }
  }

  const setPaymentValue = (instalment: string): void => {
    // eslint-disable-next-line no-plusplus
    for (let o = 0; o < options.length; o++) {
      if (options[o].value === instalment) {
        const split = options[o].label.split('X');
        const value = Number(split[0].trim()) * Number(unmask(split[1].trim()));
        onChangeFormInput(FormInputNamePayment.paymentValueWithFee)(value.toFixed(2));
        break;
      }
    }
  };

  const onChangePaymentType = (paymentType: PaymentType): void => {
    onChangeFormInput(FormInputNamePayment.paymentType)(`${paymentType}`);
    if (paymentType === PaymentType.PIX) {
      const paymentValue = Number(formData[FormInputNamePayment.paymentValue]);
      if (payment && payment.fees && payment.fees.pix && Number(payment.fees.pix) > 0) {
        const fee = Number((Number(payment.fees.pix) / 100).toFixed(2));
        onChangeFormInput(FormInputNamePayment.paymentValueWithFee)(
          (paymentValue + paymentValue * fee).toFixed(2),
        );
      } else {
        onChangeFormInput(FormInputNamePayment.paymentValueWithFee)(paymentValue.toFixed(2));
      }
    } else if (paymentType === PaymentType.BANK_SLIP) {
      const paymentValue = Number(formData[FormInputNamePayment.paymentValue]);
      if (payment && payment.fees && payment.fees.bankSlip && Number(payment.fees.bankSlip) > 0) {
        const fee = Number((Number(payment.fees.bankSlip) / 100).toFixed(2));
        onChangeFormInput(FormInputNamePayment.paymentValueWithFee)(
          (paymentValue + paymentValue * fee).toFixed(2),
        );
      } else {
        onChangeFormInput(FormInputNamePayment.paymentValueWithFee)(paymentValue.toFixed(2));
      }
    } else {
      onChangeFormInput(FormInputNamePayment.paymentValueWithFee)('');
      onChangeFormInput(FormInputNamePayment.installments)('');
    }
  };

  const mountContents = (): PaymentContent[] => {
    const contents: PaymentContent[] = [];
    if (payment && payment.fees) {
      if (payment.allowPaymentCredit) {
        contents.push({
          content: (
            <div key={1} className="mt-[50px]">
              <div className="flex">
                <IconCreditCard
                  className={classNames(
                    formData[FormInputNamePayment.paymentType] &&
                      Number(formData[FormInputNamePayment.paymentType]) === PaymentType.CREDIT_CARD
                      ? 'text-black'
                      : 'text-neutro-w-600',
                    'mt-[10px] h-[20px]',
                  )}
                />
                <h1 className="ml-[10px] font-dmsans font-normal text-neutro-b-400 text-[28px] mb-[10px ]">
                  <span>Informe a Parcela</span>
                </h1>
              </div>

              <div className="w-full md:w-[411px] mt-[14px]">
                <div className="flex flex-col justify-between">
                  <div className="space-y-5 ">
                    <div>
                      <div className="mb-[20px]">
                        <div className="block mt-[20px] mb-[20px]">
                          <div className="mb-[20px]">
                            <Select
                              name="installments"
                              label="Parcelamento"
                              placeholder="Escolha a quantidade de parcelas"
                              className={`appearance-none w-full
                            rounded-md text-heading leading-tight focus:outline-none
                            focus:shadow-outline text-xs font-dmsans font-normal`}
                              maxLength={18}
                              value={formData[FormInputNamePayment.installments]}
                              onChange={e => {
                                onChangeFormInput(FormInputNamePayment.installments)(
                                  `${e.target.value}`,
                                );
                                setPaymentValue(e.target.value);
                              }}
                              options={options}
                              error={formErrors.installments && formErrors.installments[0]}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ),
          type: PaymentType.CREDIT_CARD,
        });
      }

      if (payment.allowPaymentBankSlip) {
        contents.push({
          content: (
            <div key={3} className="md:w-[470px] mt-[50px]">
              <IconTicketPayment className="text-neutro-b-400 h-[36px] mb-[25px]" />
              <h4 className="font-dmsans font-bold text-neutro-b-400 text-base mb-[25px]">
                Quase lá!
              </h4>
              <p className="font-dmsans text-base font-normal text-neutro-w-600 mb-[40px]">
                Ao clicar em <strong>“Avançar”</strong>, o boleto aparecerá na tela e você terá 3
                dias para efetuar o pagamento.
              </p>
            </div>
          ),
          type: PaymentType.BANK_SLIP,
        });
      }

      if (payment.allowPaymentPIX) {
        contents.push({
          content: (
            <div key={4} className="md:w-[411px] mt-[50px]">
              <IconPix className="text-neutro-b-400 h-[36px] mb-[25px]" />
              <h4 className="font-dmsans font-bold text-neutro-b-400 text-base mb-[25px]">
                Quase lá!
              </h4>
              <p className="font-dmsans text-base font-normal text-neutro-w-600 mb-[40px]">
                Ao clicar em <strong>“Avançar”</strong>, o código PIX aparecerá na tela para você
                poder fazer o pagamento instantâneo.
              </p>
            </div>
          ),
          type: PaymentType.PIX,
        });
      }
    }
    return contents;
  };
  return (
    <Fragment>
      <h2 className="font-dmsans font-normal text-neutro-b-400 text-[28px]">Forma de pagamento</h2>
      <p className="font-dmsans text-base font-normal text-neutro-w-600 leading-[28px]">
        Escolha a sua forma de pagamento
      </p>
      <div className="mt-[20px]">
        <div className="tabs">
          <nav className="tabs__nav md:w-[500px] grid grid-cols-3 gap-3">
            {mountTabs().map((tab, index) => (
              <a
                key={index + 1}
                onClick={() => onChangePaymentType(tab.type)}
                className={classNames(
                  formData[FormInputNamePayment.paymentType] &&
                    Number(formData[FormInputNamePayment.paymentType]) === tab.type
                    ? 'bg-alert-info-soft text-white'
                    : 'bg-neutro-w-100 text-neutro-w-600',
                  'md:w-[150px] md:h-[100px] pt-[15px] md:pt-0 rounded-[5px] flex flex-col items-center justify-center font-dmsans font-medium cursor-pointer',
                )}
              >
                {tab.icon}
                <span className="hidden md:inline">{tab.text}</span>
              </a>
            ))}
          </nav>
          <div className="tabs__content mt-8">
            {mountContents().map((content, index: number) => (
              <div
                key={index + 1}
                className={classNames(
                  formData[FormInputNamePayment.paymentType] &&
                    Number(formData[FormInputNamePayment.paymentType]) === content.type
                    ? 'block'
                    : 'hidden',
                  'w-full',
                )}
              >
                {content.content}
              </div>
            ))}
          </div>
        </div>
      </div>
    </Fragment>
  );
};
